import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Input, List, Skeleton, Tag, Typography } from 'antd';
import { TbApi, TbCopy, TbSearch, TbTrash } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection } from '@/shared';
import { DeletePrimitiveModal } from '@/shared/components/modals/ResourceActionModals';
import { formatDate } from '@/utilities';

import { OAuthApplication } from '../../../../types';

const ManageOAuthApplications: FC = () => {
    const { token } = useAntDesignTheme();
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [oauthApplications, setOAuthApplications] = useState<OAuthApplication[]>([]);
    const [applicationToDelete, setApplicationToDelete] = useState<OAuthApplication | null>(null);

    const getOAuthApplications = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await authorizedAxiosInstance.get<OAuthApplication[]>('/api/oauth-applications');
            setOAuthApplications(data);
        } catch (error) {
            captureException(error);
            toast.error('Unable to load OAuth applications');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void getOAuthApplications();
    }, [getOAuthApplications]);

    const filteredApplications = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return oauthApplications;
        }

        return oauthApplications.filter(
            ({ name, shortDescription }) =>
                name.toLowerCase().includes(normalizedQuery) ||
                shortDescription?.toLowerCase().includes(normalizedQuery),
        );
    }, [oauthApplications, query]);

    const deleteApplication = async (application: OAuthApplication) => {
        try {
            await authorizedAxiosInstance.delete(`/api/oauth-applications/${application.id}`);
            setOAuthApplications((items) => items.filter(({ id }) => id !== application.id));
            toast('OAuth Application Deleted', { description: application.name });
        } catch (error) {
            captureException(error);
            toast.error('Unable to delete OAuth application');
            throw error;
        }
    };

    return (
        <>
            <div
                style={{
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    overflow: 'hidden',
                }}
            >
                <div style={{ padding: '0.75rem', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                    <Input
                        allowClear
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        prefix={<TbSearch style={{ color: token.colorTextTertiary }} />}
                        placeholder='Search OAuth Applications...'
                    />
                </div>
                <div style={{ minHeight: 0, flex: 1, overflowY: 'auto' }}>
                    {loading ? (
                        <div style={{ padding: '1rem' }}>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <Skeleton key={index} avatar active paragraph={{ rows: 1 }} />
                            ))}
                        </div>
                    ) : filteredApplications.length > 0 ? (
                        <List
                            dataSource={filteredApplications}
                            renderItem={(application) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            key='copy'
                                            size='small'
                                            type='text'
                                            icon={<TbCopy />}
                                            title='Copy client ID'
                                            onClick={() => {
                                                void navigator.clipboard.writeText(application.clientId);
                                                toast('Client ID Copied', { description: application.name });
                                            }}
                                        />,
                                        <Button
                                            key='delete'
                                            size='small'
                                            type='text'
                                            danger
                                            icon={<TbTrash />}
                                            title='Delete application'
                                            onClick={() => setApplicationToDelete(application)}
                                        />,
                                    ]}
                                    style={{ padding: '0.875rem 1rem' }}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar
                                                size='large'
                                                shape='square'
                                                src={application.iconUrl}
                                                icon={<TbApi />}
                                                style={{ backgroundColor: token.colorBgContainer }}
                                            />
                                        }
                                        title={
                                            <span>
                                                {application.name}{' '}
                                                <Tag color='green' bordered={false}>
                                                    {application.status ?? 'Active'}
                                                </Tag>
                                            </span>
                                        }
                                        description={
                                            <Typography.Text type='secondary'>
                                                {application.shortDescription || 'No description'}
                                                {application.createdAt &&
                                                    ` · Created ${formatDate(new Date(application.createdAt))}`}
                                            </Typography.Text>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    ) : (
                        <EmptySection
                            height='100%'
                            icon={<TbApi style={{ color: token.colorTextTertiary }} />}
                            label={query ? 'No Matching Applications' : 'No OAuth Applications'}
                            description={
                                query
                                    ? 'Try a different application name or description.'
                                    : 'Create an OAuth application to connect an external service securely.'
                            }
                        />
                    )}
                </div>
            </div>
            {applicationToDelete && (
                <DeletePrimitiveModal
                    open
                    resourceLabel='OAuth Application'
                    resourceName={applicationToDelete.name}
                    warning='Deleting this OAuth application permanently invalidates its client and existing authorizations.'
                    onCancel={() => setApplicationToDelete(null)}
                    onDelete={() => deleteApplication(applicationToDelete)}
                />
            )}
        </>
    );
};

export default ManageOAuthApplications;
